import { debounce } from "lodash-es";
import { getJitsiRoom } from "../base/conference";
import { getJitsiIdByMemberId, getPinnedParticipantsByStatus, getRemoteParticipantsByStatus } from "../base/participants";
import { StateListenerRegistry } from "../base/redux";
import { getCurrentMode, videoLayoutMode } from "../video-layout-mode";
import { VIDEO_QUALITY_LEVELS } from "./constants";

// 핀 사용자 
StateListenerRegistry.register(state => state["features/base/participants"].pinnedParticipant,
    (pinnedParticipant, store) => _updateReceiverVideoConstraints(store), {
        deepEquals: true
    }
);

StateListenerRegistry.register(state => getCurrentMode(state),
    debounce((mode, store) => {
        _updateReceiverVideoConstraints(store);
    }), 500);

StateListenerRegistry.register(state => state['features/screen-share'].sharingVideo,
    (sharingVideo, store) => {
        _updateReceiverVideoConstraints(store);
    });

StateListenerRegistry.register(state => state['features/video-layout-mode'].page,
    debounce((page, store) => {
        _updateReceiverVideoConstraints(store);
    }, {
        deepEquals: true
    }), 500);

StateListenerRegistry.register(state => state['features/video-layout-mode'].visibleRemoteParticipants,
    debounce((remoteParticipants, store) => {
        _updateReceiverVideoConstraints(store);
    }, {
        deepEquals: true
    }), 500);

StateListenerRegistry.register(state => state['features/base/participants'].bridgeParticipant.size,
    debounce((bridgeParticipant, store) => {
        _updateReceiverVideoConstraints(store);
    }), 500);

StateListenerRegistry.register(
    state => state['features/base/tracks'],
    debounce((remoteTracks, store) => {
        _updateReceiverVideoConstraints(store);
    }, {
        deepEquals: true
    }), 500);

StateListenerRegistry.register(
    state => {
        const {
            maxReceiverVideoQuality,
            preferredVideoQuality
        } = state['features/video-quality'];

        return {
            maxReceiverVideoQuality,
            preferredVideoQuality
        };
    },
    (currentState, store, previousState = {}) => {
        const { maxReceiverVideoQuality, preferredVideoQuality } = currentState;
        const changedPreferredVideoQuality = preferredVideoQuality !== previousState.preferredVideoQuality;
        const changedReceiverVideoQuality = maxReceiverVideoQuality !== previousState.maxReceiverVideoQuality;

        if (changedPreferredVideoQuality) {
            _setSenderVideoConstraint(preferredVideoQuality, store);
        }
        changedReceiverVideoQuality && _updateReceiverVideoConstraints(store);
    }, {
        deepEquals: true
    });

function _setSenderVideoConstraint(preferred, { getState, dispatch }) {
    const state = getState();
    const conference = getJitsiRoom(state)?.conference?.conference;

    if (!conference) {
        return;
    }

    conference.setSenderVideoConstraint(preferred)
        .catch(error => {
            console.log(error);
        });
}

export function _updateReceiverVideoConstraints({ getState, dispatch }) {
    const state = getState();
    const conference = getJitsiRoom(state)?.conference?.conference;

    if (!conference) return;
    const { isDualMonitor, visibleRemoteParticipants } = state['features/video-layout-mode'];
    const mode = getCurrentMode(state);

    const receiverConstraints = {
        // lastN: 20,
        constraints: {},
        defaultConstraints: { 'maxHeight': isDualMonitor ? VIDEO_QUALITY_LEVELS.LOW : VIDEO_QUALITY_LEVELS.NONE },
        onStageEndpoints: [],
        selectedEndpoints: []
    }; 

    let constraintsList = {
        [VIDEO_QUALITY_LEVELS.LOW]: new Set(),
        [VIDEO_QUALITY_LEVELS.STANDARD]: new Set(),
        [VIDEO_QUALITY_LEVELS.HIGH]: new Set()        
    };

    const pinnedParticipantIds = getPinnedParticipantsByStatus(getState(), true).map(p => p.user_uuid);
    if (videoLayoutMode.grid !== mode && videoLayoutMode.seminar !== mode) {
        [ ...visibleRemoteParticipants ].map(item => constraintsList[VIDEO_QUALITY_LEVELS.LOW].add(item));
    }

    switch (mode) {
        case videoLayoutMode.grid:
            if (visibleRemoteParticipants.size < 10) constraintsList[VIDEO_QUALITY_LEVELS.HIGH] = new Set(visibleRemoteParticipants);
            else if (visibleRemoteParticipants.size < 20) constraintsList[VIDEO_QUALITY_LEVELS.STANDARD] = new Set(visibleRemoteParticipants);
            else constraintsList[VIDEO_QUALITY_LEVELS.LOW] = new Set(visibleRemoteParticipants);
            break;

        case videoLayoutMode.pin:
        case videoLayoutMode.seminar:
            constraintsList[VIDEO_QUALITY_LEVELS.HIGH] = new Set(pinnedParticipantIds);
            break;

        case videoLayoutMode.voice:
            constraintsList[VIDEO_QUALITY_LEVELS.HIGH] = new Set(pinnedParticipantIds);

            const { dominantSpeaker } = state['features/base/participants'];
            dominantSpeaker && constraintsList[VIDEO_QUALITY_LEVELS.HIGH].add(dominantSpeaker);
            break;

        case videoLayoutMode.screen: 
            const { track_id } = state['features/screen-share'].sharingVideo;

            if (track_id) {
                receiverConstraints.constraints[track_id] = { 'maxHeight' : VIDEO_QUALITY_LEVELS.HIGH }
            }
        case videoLayoutMode.document:
        case videoLayoutMode.white:
        case videoLayoutMode.note:
        case videoLayoutMode.vod:
            [...pinnedParticipantIds].map(item => constraintsList[VIDEO_QUALITY_LEVELS.LOW].add(item));
            break;
    }    

    let onStageEndpoints = [];
    let onSelectEndpoints = [];
    let lastN = 0;
    Object.keys(constraintsList).forEach(quality => {
        constraintsList[quality].forEach(participantId => {
            const participant = getJitsiIdByMemberId(state, participantId);
            if (participant) {
                if (quality == VIDEO_QUALITY_LEVELS.HIGH) {
                    if (videoLayoutMode.pin === mode || videoLayoutMode.voice === mode) {
                        receiverConstraints.onStageEndpoints.push(participant);
                    }
                }

                if (videoLayoutMode.grid === mode) {
                    receiverConstraints.selectedEndpoints.push(participant);
                }

                receiverConstraints.constraints[participant] = { 'maxHeight' : quality };
                lastN++;
            }
        });
    });
    
    // receiverConstraints.lastN = lastN;
    try {
        conference.setReceiverConstraints(receiverConstraints);
    } catch (error) {
        console.log(error)
    }
}